/**
 * Camada vetorial interativa de realce de bairros e loteamentos de Artur Nogueira.
 *
 * Carrega sob demanda as geometrias oficiais de `mapa_abairramento` (`/maps/bairros.geojson`).
 * Ao clicar no nome ou em qualquer área de um bairro, ilumina o polígono com um preenchimento
 * translúcido sutil e contorno elegante, permitindo ao usuário visualizar instantaneamente
 * os limites geográficos oficiais sem poluir as indicações municipais.
 *
 * @module components/NeighborhoodHighlightLayer
 */

import { memo, useEffect, useRef, useState } from "react";
import { GeoJSON } from "react-leaflet";
import L from "leaflet";
import { getNeighborhoodFeatureStyle } from "../constants/mapConfig";

export { getNeighborhoodFeatureStyle };

/**
 * Componente React-Leaflet que gerencia o ciclo de vida e interação da malha de bairros.
 *
 * @param {object} props
 * @param {object|null} props.selectedNeighborhood Bairro atualmente destacado
 * @param {(neighborhood: object|null) => void} props.onSelectNeighborhood Callback disparado ao selecionar/desmarcar
 * @param {boolean} [props.isDarkMode=false] Tema ativo
 * @param {string} [props.url="/maps/bairros.geojson"] URL do arquivo GeoJSON
 */
function NeighborhoodHighlightLayer({
  selectedNeighborhood,
  onSelectNeighborhood,
  isDarkMode = false,
  url = "/maps/bairros.geojson",
}) {
  const [geoData, setGeoData] = useState(null);
  const geoJsonRef = useRef(null);

  // 1. Carregamento sob demanda do GeoJSON de bairros
  useEffect(() => {
    let active = true;
    fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error(`Falha HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => {
        if (active && data && data.type === "FeatureCollection") {
          setGeoData(data);
        }
      })
      .catch((err) => {
        if (import.meta.env.DEV) {
          console.debug("[NeighborhoodHighlightLayer] Não foi possível carregar bairros:", err);
        }
      });

    return () => {
      active = false;
    };
  }, [url]);

  // 2. Atualização reativa de estilo nos polígonos sem remontar o layer
  useEffect(() => {
    if (!geoJsonRef.current) return;
    geoJsonRef.current.setStyle((feature) =>
      getNeighborhoodFeatureStyle(feature, selectedNeighborhood, isDarkMode)
    );
  }, [selectedNeighborhood, isDarkMode]);

  if (!geoData) return null;

  return (
    <GeoJSON
      ref={geoJsonRef}
      key="neighborhood-highlight-layer"
      data={geoData}
      style={(feature) => getNeighborhoodFeatureStyle(feature, selectedNeighborhood, isDarkMode)}
      onEachFeature={(feature, layer) => {
        layer.on({
          click: (e) => {
            if (e.originalEvent) {
              L.DomEvent.stopPropagation(e.originalEvent);
            }
            const nbInfo = {
              id: feature.id,
              name: feature.properties?.name || "Bairro",
              fullName: feature.properties?.fullName || feature.properties?.name || "Bairro",
              center: feature.properties?.center,
            };
            onSelectNeighborhood?.(nbInfo);
          },
        });
      }}
    />
  );
}

export default memo(NeighborhoodHighlightLayer);
